import { AnchorButton, Button } from "@svea/design-system-components-react";
import { ButtonGroup, Pill, Stack, Text, useText } from "@sveabank/cc-common";
import { format } from "date-fns";
import { Link } from "react-router";
import Paper from "../../components/paper";
import { useUser } from "../../hooks/useUser";
import {
	useDeleteApplicationMutation,
	useLazyFetchApplicationRevisionQuery,
} from "../../services/application";
import { resetPossibleGuarantors } from "../../slices/guarantorSlice";
import { useAppDispatch } from "../../store";
import type { FullApplicationData } from "../../types/application";
import { getApplicationItemStatus } from "./utils";

type Props = { application: FullApplicationData };

const OngoingApplication = ({ application }: Props) => {
	const [t, texts] = useText();
	const dispatch = useAppDispatch();

	const [fetchRevisionQuery] = useLazyFetchApplicationRevisionQuery();
	const [deleteApplicationMutation, { isLoading: isDeletingApplication }] =
		useDeleteApplicationMutation();

	const deleteApplication = async (applicationId: string) => {
		await fetchRevisionQuery(applicationId).unwrap();
		await deleteApplicationMutation(applicationId).unwrap();
		dispatch(resetPossibleGuarantors());
	};

	const { getLoggedInUser } = useUser();
	const currentUserSsn = getLoggedInUser().bankIdUser.bankIdNumber;
	const { description, pillText, pillStatus } = getApplicationItemStatus(
		currentUserSsn,
		application,
	);

	return (
		<Paper padding="base" key={application.applicationId}>
			<Stack gap={2}>
				<Pill
					text={`${t(pillText)} ${format(application.initiatedAt, "yyyy-MM-dd")}`}
					status={pillStatus}
				/>
				{!!description && <Text className="text-sm">{t(description)}</Text>}
				<Text component="h4" className="text-text-primary text-xl" bold>
					{application.name}
				</Text>
				<Text>{application.orgNrDisplayName}</Text>
				<ButtonGroup align="start" row>
					<AnchorButton
						as={Link}
						to={`/application/${application.applicationId}`}
						state={{ redirectToStep: true }}
						iconEnd="chevronRight"
					>
						{t(texts.common.continue)}
					</AnchorButton>
					<Button
						onPress={() => deleteApplication(application.applicationId)}
						variant="secondary"
						iconStart="trash"
						isPending={isDeletingApplication}
						data-tracking-action="Onboarding: Application Delete"
						data-tracking-name="Delete Application"
					>
						{t(texts.common.deleteApplication)}
					</Button>
				</ButtonGroup>
			</Stack>
		</Paper>
	);
};

export default OngoingApplication;
